import {ElementUtil} from './elementUtil.js';
import {GridColumnLayoutUtil} from './gridColumnLayoutUtil.js';
import {pageRegistry} from './pageRegistry.js';
import {sanitizeHtmlFromStoredText} from './browserStorageSanitizer.js';
import {TimeUtil} from './timeUtil.js';

export var ExportUtil = {

	/**
	 * Escape text for safe use in HTML text nodes (not for attributes).
	 */
	escapeHtml: function(value) {
		if (value === null || value === undefined) {
			return '';
		}
		var text = sanitizeHtmlFromStoredText(String(value)) || '';
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	},
	
	/**
	 * Client side export (assumes data is formated correctly)
	 * see https://stackoverflow.com/questions/19327749/javascript-blob-filename-without-link/19328891#19328891
	 * @param {type} fileInfo (data, fileName, type (mimetype))
	 * @returns void
	 */
	savefile: function(fileInfo) {
		var anchor = document.createElement("a");
		document.body.appendChild(anchor);		
		anchor.style = "display: none";
		
		var blob = new Blob([fileInfo.data], {type: fileInfo.type}); //"octet/stream"
        var url = window.URL.createObjectURL(blob);
        anchor.href = url;
        anchor.download = fileInfo.fileName;
        anchor.click();
		setTimeout(function(){
			window.URL.revokeObjectURL(url);
			anchor.remove();
		});
		
	},

	/**
	 * Escape a CSV cell value.
	 */
	csvEscape: function(value) {
		if (value === null || value === undefined) {
			return '';
		}
		var text = String(value);
		if (text.indexOf('"') !== -1 || text.indexOf(',') !== -1 || text.indexOf('\n') !== -1 || text.indexOf('\r') !== -1) {
			return '"' + text.replace(/"/g, '""') + '"';
		}
		return text;
	},

	/**
	 * Build a download filename from the active page title: "<page name>-export.csv".
	 */
	buildPageExportFileName: function(grid) {
		var pageName = '';
		var activeEntry = pageRegistry && pageRegistry.getActiveEntry ? pageRegistry.getActiveEntry() : null;
		if (activeEntry && activeEntry.title) {
			pageName = activeEntry.title;
		} else if (activeEntry && activeEntry.pageKey) {
			pageName = activeEntry.pageKey;
		} else if (GridColumnLayoutUtil && GridColumnLayoutUtil.resolvePageKey) {
			pageName = GridColumnLayoutUtil.resolvePageKey(grid) || '';
		}

		pageName = sanitizeHtmlFromStoredText(String(pageName || 'data')) || 'data';
		pageName = pageName
			.replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
			.replace(/\s+/g, ' ')
			.trim();
		if (!pageName) {
			pageName = 'data';
		}
		return pageName + '-export.csv';
	},

	/**
	 * Flatten nested objects into dotted keys for CSV columns.
	 */
	flattenValue: function(value, prefix, target) {
		var ExportUtil = this;
		if (value === null || value === undefined) {
			target[prefix] = '';
			return;
		}
		if (Array.isArray(value)) {
			target[prefix] = value.map(function(entry) {
				if (entry !== null && typeof entry === 'object') {
					return JSON.stringify(entry);
				}
				return entry;
			}).join('; ');
			return;
		}
		if (typeof value === 'string' && TimeUtil.looksLikeDateTimeValue(value)) {
			target[prefix] = TimeUtil.formatDateStandard(value);
			return;
		}
		if (typeof value === 'object') {
			if (value instanceof Date) {
				target[prefix] = TimeUtil.formatDateStandard(value);
				return;
			}
			Object.keys(value).forEach(function(key) {
				var nextPrefix = prefix ? (prefix + '.' + key) : key;
				ExportUtil.flattenValue(value[key], nextPrefix, target);
			});
			return;
		}
		target[prefix] = value;
	},

	/**
	 * Export current page grid.items as CSV with all flattened fields.
	 * @param {Array} items
	 * @param {string} fileName
	 */
	exportGridItemsAsCsv: function(items, fileName) {
		var ExportUtil = this;
		if (!items || !items.length) {
			window.DL.MessageUtil.showToast('Export', 'No rows on the current page to export.', {
				theme: 'contrast',
				position: 'top-stretch'
			});
			return;
		}

		var flattenedRows = [];
		var headerMap = {};
		items.forEach(function(item) {
			var flat = {};
			ExportUtil.flattenValue(item, '', flat);
			Object.keys(flat).forEach(function(key) {
				if (key) {
					headerMap[key] = true;
				}
			});
			flattenedRows.push(flat);
		});

		var headers = Object.keys(headerMap).sort();
		var lines = [];
		lines.push(headers.map(ExportUtil.csvEscape).join(','));
		flattenedRows.forEach(function(row) {
			lines.push(headers.map(function(header) {
				return ExportUtil.csvEscape(row[header]);
			}).join(','));
		});

		ExportUtil.savefile({
			data: lines.join('\n') + '\n',
			fileName: fileName || 'dl-page-export.csv',
			type: 'text/csv'
		});
	},

	/**
	 * Build the standard footer: export toolbar + divider + paging toolbar.
	 * @param {object} options
	 * @param {HTMLElement} options.pagingToolbar
	 * @param {HTMLElement} options.grid
	 * @param {string} [options.itemid]
	 * @param {boolean} [options.showDetail]
	 * @param {Function} [options.onDetail]
	 * @param {Array} [options.exportToolbarChildren]
	 * @returns {{footerContainer: HTMLElement, exportToolbar: HTMLElement}}
	 */
	createExportFooter: function(options) {
		options = options || {};

		var exportToolbar = ElementUtil.create({
			tag: 'dl-export-toolbar',
			itemid: options.itemid || 'primary',
			children: options.exportToolbarChildren || [],
			postinit: function(toolbar) {
				if (options.showDetail === false) {
					toolbar.showDetail = false;
				}
				if (options.showCSV === false) {
					toolbar.showCSV = false;
				}
			},
			listeners: {
				ready: function() {
					if (options.grid) {
						exportToolbar.updateGrid(options.grid);
					}
				},
				'click-detail': function() {
					if (typeof options.onDetail === 'function') {
						options.onDetail();
					}
				}
			}
		});

		var footerChildren = [
			exportToolbar,
			{
				tag: 'div',
				style: 'border-top: 1px solid lightgrey;'
			}
		];
		if (options.pagingToolbar) {
			footerChildren.push(options.pagingToolbar);
		}

		var footerContainer = ElementUtil.create({
			tag: 'div',
			slot: 'footer',
			children: footerChildren
		});

		return {
			footerContainer: footerContainer,
			exportToolbar: exportToolbar
		};
	}
	
};
